import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Clock, Truck, CreditCard, Shield, Bell, Camera, Utensils, Eye, EyeOff, Mail, AlertCircle, Wallet, ArrowUpRight, ArrowDownLeft, Gift, BadgeCheck, FileText, Upload } from 'lucide-react';
import { PageSkeleton } from '../DashboardLoader';
import {
  getRestaurantProfile,
  updateRestaurantProfile,
  getRestaurantHours,
  updateRestaurantHours,
  getRestaurantDashboardStats,
  getRestaurantWallet,
  getWalletTransactions,
  changePassword,
  getActiveSessions,
  terminateSession,
  terminateAllSessions,
  get2FAStatus,
  enable2FA,
  disable2FA,
  uploadRestaurantDocument,
  getMyRestaurantDocuments,
} from '../../../services/restaurantApi';
import { toastSuccess, toastError, toastWarning } from '../../../utils/toast';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const CARD = { background: '#FCFBF5', borderRadius: '30px', display: 'flex', flexDirection: 'column' };
const CARD_HEADER = { padding: 'clamp(20px, 2.2vw, 28px)', borderBottom: '1px solid rgba(5,16,4,0.1)', display: 'flex', flexDirection: 'column', gap: '6px' };
const CARD_TITLE = { margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '19px', letterSpacing: '-0.01em' };
const CARD_SUBTITLE = { margin: 0, fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.62)' };
const CARD_BODY = { padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '22px' };

const FIELD_LABEL = { display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' };
const INPUT_STYLE = { width: '100%', background: '#FBF9D9', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 20px', minHeight: '50px', fontSize: '14.5px', color: '#051004', outline: 'none', fontFamily: 'Poppins, sans-serif', boxSizing: 'border-box' };
const TEXTAREA_STYLE = { ...INPUT_STYLE, borderRadius: '20px', padding: '14px 20px', minHeight: 'auto', resize: 'vertical', fontFamily: 'Poppins, sans-serif' };
const HINT_STYLE = { margin: '8px 0 0', fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' };

const PRIMARY_BTN = { border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px', minHeight: '46px', padding: '0 24px', borderRadius: '999px', whiteSpace: 'nowrap' };
const GHOST_BTN = { border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px', whiteSpace: 'nowrap' };
const DANGER_GHOST_BTN = { border: '1px solid rgba(163,44,15,0.3)', cursor: 'pointer', background: 'transparent', color: '#A32C0F', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', minHeight: '38px', padding: '0 16px', borderRadius: '999px', whiteSpace: 'nowrap' };

const disabledStyle = (base, disabled) => (disabled ? { ...base, opacity: 0.55, cursor: 'not-allowed' } : base);

const toggleTrackStyle = (on) => ({ flex: 'none', border: 'none', cursor: 'pointer', width: '50px', height: '28px', borderRadius: '999px', padding: '3px', display: 'flex', background: on ? '#72B744' : 'rgba(5,16,4,0.22)', transition: 'background 180ms ease' });
const toggleKnobStyle = (on) => ({ width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', transform: on ? 'translateX(22px)' : 'translateX(0)', transition: 'transform 180ms ease' });

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    style={disabledStyle(toggleTrackStyle(checked), disabled)}
  >
    <span style={toggleKnobStyle(checked)} />
  </button>
);

const Field = ({ label, hint, children }) => (
  <div>
    <label style={FIELD_LABEL}>{label}</label>
    {children}
    {hint && <p style={HINT_STYLE}>{hint}</p>}
  </div>
);

const NewRestaurantSettings = () => {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'profile');
  // Section demandée depuis le menu du profil (?section=...), même si la page est déjà ouverte
  useEffect(() => {
    const section = searchParams.get('section');
    if (section) setActiveSection(section);
  }, [searchParams]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    cuisine_type: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    latitude: '',
    longitude: '',
    description: '',
    capacity: 50,
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    banner_url: '',
    restaurant_image_url: ''
  });
  const [bannerPreview, setBannerPreview] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [certification, setCertification] = useState({ isCertified: false, certifiedAt: null });
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [restaurantImagePreview, setRestaurantImagePreview] = useState(null);
  const [uploadingRestaurantImage, setUploadingRestaurantImage] = useState(false);
  const [hours, setHours] = useState({});
  const [deliverySettings, setDeliverySettings] = useState({
    enabled: true,
    fee: 0,
    minOrder: 0,
    acceptCOD: true,
    acceptPickup: false
  });
  const [securitySettings, setSecuritySettings] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
    showPassword: false
  });
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(null); // 'enable' | 'disable' | null
  const [twoFactorModalPassword, setTwoFactorModalPassword] = useState('');
  const [twoFactorBackupCodes, setTwoFactorBackupCodes] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    email: {
      orders: true,
      reviews: true,
      promotions: true,
      payments: true,
      announcements: true
    },
    push: {
      orders: true,
      reviews: true,
      promotions: false,
      payments: true,
      announcements: true
    }
  });
  const [walletInfo, setWalletInfo] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loyaltySettings, setLoyaltySettings] = useState({
    enabled: true,
    trancheEuros: 10,
    pointsPerTranche: 1,
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);

        // Charger le profil du restaurant
        const profile = await getRestaurantProfile();

        // Charger les horaires
        const hoursData = await getRestaurantHours();

        // Charger les stats pour obtenir les infos de base
        const stats = await getRestaurantDashboardStats();

        // Formater les horaires
        const formattedHours = {};
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        days.forEach((day, index) => {
          const dayHours = Array.isArray(hoursData) ? hoursData.find(h => h.day_of_week === index) : null;
          formattedHours[day] = dayHours ? {
            open: dayHours.open_time || '11:00',
            close: dayHours.close_time || '22:00',
            closed: dayHours.is_closed !== undefined ? dayHours.is_closed : (dayHours.is_open === false)
          } : {
            open: '11:00',
            close: '22:00',
            closed: false
          };
        });
        setHours(formattedHours);

        // Mettre à jour formData avec les données du backend
        setFormData({
          name: profile?.name || stats?.restaurant_name || '',
          cuisine_type: profile?.cuisine_type || stats?.cuisine_type || '',
          email: profile?.email || stats?.email || '',
          phone: profile?.phone || stats?.phone || '',
          address: profile?.address || '',
          city: profile?.city || '',
          postal_code: profile?.postal_code || '',
          country: profile?.country || '',
          latitude: profile?.latitude != null ? String(profile.latitude) : '',
          longitude: profile?.longitude != null ? String(profile.longitude) : '',
          description: profile?.description || profile?.bio || '',
          capacity: profile?.capacity || 50,
          website: profile?.website_url || profile?.website || '',
          facebook: profile?.facebook_link || profile?.facebook_url || '',
          instagram: profile?.instagram_link || profile?.instagram_url || '',
          twitter: '',
          linkedin: '',
          banner_url: profile?.banner_url || profile?.banner_image_url || '',
          restaurant_image_url: profile?.restaurant_image_url || (profile?.images_by_type?.restaurant?.[0]?.image_url ?? '')
        });

        setCertification({
          isCertified: profile?.is_certified === true,
          certifiedAt: profile?.certified_at || null,
        });

        if (profile?.notification_settings?.email && profile?.notification_settings?.push) {
          setNotificationSettings(profile.notification_settings);
        }

        // Initialiser la prévisualisation de la bannière
        if (profile?.banner_url || profile?.banner_image_url) {
          setBannerPreview(profile?.banner_url || profile?.banner_image_url);
        }

        // Initialiser la prévisualisation de l'image principale restaurant
        if (profile?.restaurant_image_url || (profile?.images_by_type?.restaurant && profile.images_by_type.restaurant.length > 0)) {
          const mainImage =
            profile?.restaurant_image_url ||
            profile.images_by_type.restaurant[0]?.image_url;
          if (mainImage) {
            setRestaurantImagePreview(mainImage);
          }
        }

        // Paramètres de livraison
        setDeliverySettings({
          enabled: profile?.delivery_available !== false,
          fee: profile?.delivery_fee || stats?.delivery_fee || 0,
          minOrder: profile?.minimum_order || stats?.minimum_order || 0,
          acceptCOD: profile?.accept_cash_on_delivery !== false,
          acceptPickup: profile?.accept_pickup === true
        });

        setLoyaltySettings({
          enabled: profile?.loyalty_enabled !== false,
          trancheEuros:
            profile?.loyalty_tranche_euros != null ? Number(profile.loyalty_tranche_euros) : 10,
          pointsPerTranche:
            profile?.loyalty_points_per_tranche != null
              ? Number(profile.loyalty_points_per_tranche)
              : 1,
        });

        // Charger le statut 2FA
        try {
          const twoFAData = await get2FAStatus();
          setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: twoFAData.enabled || false }));
        } catch {}

        // Charger les informations de paiement
        try {
          const wallet = await getRestaurantWallet();
          setWalletInfo(wallet);
          const transactions = await getWalletTransactions(0, 5);
          setRecentTransactions(transactions || []);
        } catch (error) {
          console.error('Erreur lors du chargement du wallet:', error);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const loadDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const data = await getMyRestaurantDocuments();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleDocumentUpload = async (documentType, file) => {
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toastWarning('Format non supporté (PDF, JPG ou PNG uniquement).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toastWarning('Le document ne doit pas dépasser 10MB.');
      return;
    }
    setUploadingDocType(documentType);
    try {
      await uploadRestaurantDocument(documentType, file);
      toastSuccess('Document envoyé, en attente de vérification par l\'admin.');
      await loadDocuments();
    } catch (error) {
      toastError(error.response?.data?.detail || error.message || 'Erreur lors de l\'envoi du document.');
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /** Redimensionne une image pour optimiser la taille (max 1920px largeur, qualité 0.85) */
  const resizeImageIfNeeded = (f) => {
    return new Promise((resolve) => {
      if (f.size <= 800 * 1024) {
        resolve(f);
        return;
      }
      const img = document.createElement('img');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const url = URL.createObjectURL(f);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const maxW = 1920;
        let w = img.width;
        let h = img.height;
        if (w > maxW) {
          h = (h * maxW) / w;
          w = maxW;
        }
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], f.name, { type: f.type || 'image/jpeg' }) : f),
          'image/jpeg',
          0.85
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(f);
      };
      img.src = url;
    });
  };

  const handleBannerUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toastWarning('Le fichier doit être une image.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning('L\'image ne doit pas dépasser 10 Mo.');
      return;
    }

    try {
      setUploadingBanner(true);

      const fileToUpload = await resizeImageIfNeeded(file);

      const uploadData = new FormData();
      uploadData.append('file', fileToUpload);
      uploadData.append('folder', 'restaurants/banners');

      // Upload vers Digital Ocean Spaces
      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const response = await fetch('http://localhost:8000/management/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadData
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload');
      }

      const result = await response.json();

      // Mettre à jour le form avec l'URL uploadée
      setFormData(prev => ({ ...prev, banner_url: result.image_url }));
      setBannerPreview(result.image_url);

    } catch (error) {
      console.error('Erreur upload bannière:', error);
      toastError('Erreur lors de l\'upload de la bannière.');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleRestaurantImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toastWarning('Le fichier doit être une image.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastWarning("L'image ne doit pas dépasser 10 Mo.");
      return;
    }

    try {
      setUploadingRestaurantImage(true);

      const fileToUpload = await resizeImageIfNeeded(file);

      const uploadData = new FormData();
      uploadData.append('file', fileToUpload);
      uploadData.append('folder', 'restaurants/main');

      const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
      const uploadResponse = await fetch('http://localhost:8000/management/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadData
      });

      if (!uploadResponse.ok) {
        throw new Error("Erreur lors de l'upload");
      }

      const uploadResult = await uploadResponse.json();
      const imageUrl = uploadResult.image_url;

      setFormData(prev => ({ ...prev, restaurant_image_url: imageUrl }));
      setRestaurantImagePreview(imageUrl);

      // Enregistrer l'image dans restaurant_images avec le type 'restaurant'
      try {
        await fetch('http://localhost:8000/management/images', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            image_url: imageUrl,
            image_type: 'restaurant',
            is_primary: true,
            sort_order: 0
          })
        });
      } catch (err) {
        console.error("Erreur lors de l'enregistrement de l'image restaurant:", err);
      }
    } catch (error) {
      console.error("Erreur upload image restaurant:", error);
      toastError("Erreur lors de l'upload de l'image principale du restaurant.");
    } finally {
      setUploadingRestaurantImage(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    // Validation des champs obligatoires
    if (!formData.name?.trim()) {
      toastError('Veuillez renseigner le nom du restaurant.');
      return;
    }
    if (!formData.email?.trim()) {
      toastError('Veuillez renseigner l\'email.');
      return;
    }
    try {
      setSaving(true);

      const profileData = {
        name: formData.name,
        description: formData.description,
        bio: formData.description,
        cuisine_type: formData.cuisine_type,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        country: formData.country,
        latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
        website_url: formData.website,
        facebook_link: formData.facebook,
        instagram_link: formData.instagram,
        banner_image_url: formData.banner_url,
        delivery_fee: deliverySettings.fee,
        minimum_order: deliverySettings.minOrder,
        delivery_available: deliverySettings.enabled,
        accept_cash_on_delivery: deliverySettings.acceptCOD,
        accept_pickup: deliverySettings.acceptPickup,
        loyalty_enabled: loyaltySettings.enabled,
        loyalty_tranche_euros: Math.max(0.01, Number(loyaltySettings.trancheEuros) || 10),
        loyalty_points_per_tranche: Math.max(0, parseInt(String(loyaltySettings.pointsPerTranche), 10) || 0),
      };

      await updateRestaurantProfile(profileData);

      if (activeSection === 'hours' || Object.keys(hours).length > 0) {
        const hoursData = {};
        Object.keys(hours).forEach(day => {
          hoursData[day] = {
            open_time: hours[day].open,
            close_time: hours[day].close,
            is_open: !hours[day].closed
          };
        });
        await updateRestaurantHours(hoursData);
      }

      toastSuccess('Paramètres sauvegardés avec succès.');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toastError(error.response?.data?.detail || 'Erreur lors de la sauvegarde des paramètres. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const saveLoyaltyOnly = async () => {
    try {
      setSaving(true);
      await updateRestaurantProfile({
        loyalty_enabled: loyaltySettings.enabled,
        loyalty_tranche_euros: Math.max(0.01, Number(loyaltySettings.trancheEuros) || 10),
        loyalty_points_per_tranche: Math.max(0, parseInt(String(loyaltySettings.pointsPerTranche), 10) || 0),
      });
      toastSuccess('Paramètres fidélité enregistrés.');
    } catch (error) {
      console.error('Erreur fidélité:', error);
      toastError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const settingsSections = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'hours', label: 'Horaires', icon: Clock },
    { id: 'delivery', label: 'Livraison', icon: Truck },
    { id: 'loyalty', label: 'Fidélité', icon: Gift },
    { id: 'payment', label: 'Paiement', icon: CreditCard },
    { id: 'certification', label: 'Certification', icon: BadgeCheck },
    { id: 'security', label: 'Sécurité', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  const dayLabels = {
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche'
  };

  const notificationLabels = {
    orders: 'Nouvelles commandes',
    reviews: 'Nouveaux avis',
    promotions: 'Promotions et offres',
    payments: 'Paiements et transactions',
    announcements: 'Annonces importantes'
  };

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>Espace pro</span>
        <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Paramètres</h1>
        <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.62)' }}>Configurez les paramètres de votre restaurant</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Sidebar */}
        <div className="w-full lg:w-60 lg:flex-shrink-0" style={{ background: '#FCFBF5', borderRadius: '26px', padding: '10px', position: 'sticky', top: '84px' }}>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {settingsSections.map((section) => {
              const Icon = section.icon;
              const active = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left',
                    fontSize: '14px', padding: '11px 16px', borderRadius: '999px', minHeight: '44px',
                    border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif',
                    background: active ? PRIMARY : 'transparent',
                    color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {activeSection === 'profile' && (
            <div style={CARD}>
              <div style={CARD_HEADER}>
                <h3 style={CARD_TITLE}>Informations du restaurant</h3>
                <p style={CARD_SUBTITLE}>Modifiez les informations de base de votre restaurant</p>
              </div>
              <div style={CARD_BODY}>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                  {/* Logo Section */}
                  <div className="flex items-center gap-5">
                    <div style={{ position: 'relative' }}>
                      <div style={{ width: '76px', height: '76px', background: '#F4F1E2', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Utensils size={26} style={{ color: 'rgba(5,70,37,0.4)' }} />
                      </div>
                      <button type="button" style={{ position: 'absolute', bottom: '-6px', right: '-6px', border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', borderRadius: '999px', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Camera size={14} />
                      </button>
                    </div>
                    <div>
                      <h4 style={{ margin: '0 0 2px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>Logo du restaurant</h4>
                      <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>JPG, PNG ou SVG. Max 2MB.</p>
                    </div>
                  </div>

                  {/* Banner Section */}
                  <div>
                    <label style={FIELD_LABEL}>Bannière du restaurant</label>
                    <div style={{ position: 'relative' }}>
                      {bannerPreview || formData.banner_url ? (
                        <div className="group relative" style={{ width: '100%', height: '200px', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(5,16,4,0.1)' }}>
                          <img
                            src={bannerPreview || formData.banner_url}
                            alt="Bannière du restaurant"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-opacity flex items-center justify-center">
                            <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleBannerUpload}
                                className="hidden"
                                disabled={uploadingBanner}
                              />
                              <div className="flex items-center gap-2" style={{ background: 'rgba(252,251,245,0.95)', borderRadius: '999px', padding: '10px 18px' }}>
                                <Camera size={16} />
                                <span style={{ fontSize: '13.5px', fontWeight: 600 }}>
                                  {uploadingBanner ? 'Upload en cours...' : 'Changer la bannière'}
                                </span>
                              </div>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '200px', border: '2px dashed rgba(5,16,4,0.22)', borderRadius: '24px', cursor: 'pointer', background: '#FBF9D9' }}>
                          <Camera size={32} style={{ color: 'rgba(5,16,4,0.3)', marginBottom: '10px' }} />
                          <p style={{ margin: '0 0 4px', fontSize: '13.5px', fontWeight: 600 }}>Cliquez pour uploader ou glissez-déposez</p>
                          <p style={{ margin: 0, fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>PNG, JPG, WEBP jusqu'à 10MB</p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleBannerUpload}
                            className="hidden"
                            disabled={uploadingBanner}
                          />
                        </label>
                      )}
                      {uploadingBanner && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(252,251,245,0.85)', borderRadius: '24px' }}>
                          <div className="flex flex-col items-center">
                            <div className="animate-spin" style={{ width: '28px', height: '28px', borderRadius: '999px', border: '2px solid rgba(5,16,4,0.14)', borderTopColor: PRIMARY, marginBottom: '8px' }} />
                            <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.6)' }}>Upload en cours...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <p style={HINT_STYLE}>
                      La bannière sera affichée en haut de la page de votre restaurant. Dimensions recommandées: 1920x600px
                    </p>
                  </div>

                  {/* Restaurant main image section */}
                  <div>
                    <label style={FIELD_LABEL}>Image principale du restaurant</label>
                    <div style={{ position: 'relative' }}>
                      {restaurantImagePreview || formData.restaurant_image_url ? (
                        <div className="group relative" style={{ width: '100%', height: '200px', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(5,16,4,0.1)' }}>
                          <img
                            src={restaurantImagePreview || formData.restaurant_image_url}
                            alt="Image principale du restaurant"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-opacity flex items-center justify-center">
                            <label className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleRestaurantImageUpload}
                                className="hidden"
                                disabled={uploadingRestaurantImage}
                              />
                              <div className="flex items-center gap-2" style={{ background: 'rgba(252,251,245,0.95)', borderRadius: '999px', padding: '10px 18px' }}>
                                <Camera size={16} />
                                <span style={{ fontSize: '13.5px', fontWeight: 600 }}>
                                  {uploadingRestaurantImage ? 'Upload en cours...' : "Changer l'image principale"}
                                </span>
                              </div>
                            </label>
                          </div>
                        </div>
                      ) : (
                        <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '200px', border: '2px dashed rgba(5,16,4,0.22)', borderRadius: '24px', cursor: 'pointer', background: '#FBF9D9' }}>
                          <Camera size={32} style={{ color: 'rgba(5,16,4,0.3)', marginBottom: '10px' }} />
                          <p style={{ margin: '0 0 4px', fontSize: '13.5px', fontWeight: 600 }}>Cliquez pour uploader ou glissez-déposez</p>
                          <p style={{ margin: 0, fontSize: '12px', color: 'rgba(5,16,4,0.5)' }}>PNG, JPG, WEBP jusqu'à 10MB</p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleRestaurantImageUpload}
                            className="hidden"
                            disabled={uploadingRestaurantImage}
                          />
                        </label>
                      )}
                      {uploadingRestaurantImage && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(252,251,245,0.85)', borderRadius: '24px' }}>
                          <div className="flex flex-col items-center">
                            <div className="animate-spin" style={{ width: '28px', height: '28px', borderRadius: '999px', border: '2px solid rgba(5,16,4,0.14)', borderTopColor: PRIMARY, marginBottom: '8px' }} />
                            <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.6)' }}>Upload en cours...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <p style={HINT_STYLE}>
                      Cette image sera utilisée comme visuel principal de votre restaurant (vignettes, sections visuelles, etc.).
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Nom du restaurant">
                      <input type="text" name="name" value={formData.name} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                    <Field label="Type de cuisine">
                      <select name="cuisine_type" value={formData.cuisine_type} onChange={handleChange} style={INPUT_STYLE}>
                        <option value="">Sélectionner un type</option>
                        <option value="Française">Française</option>
                        <option value="Italienne">Italienne</option>
                        <option value="Asiatique">Asiatique</option>
                        <option value="Méditerranéenne">Méditerranéenne</option>
                        <option value="Africaine">Africaine</option>
                        <option value="Américaine">Américaine</option>
                        <option value="Japonaise">Japonaise</option>
                        <option value="Chinoise">Chinoise</option>
                        <option value="Indienne">Indienne</option>
                        <option value="Mexicaine">Mexicaine</option>
                        <option value="Libanaise">Libanaise</option>
                        <option value="Grecque">Grecque</option>
                        <option value="Espagnole">Espagnole</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </Field>
                    <Field label="Email">
                      <input type="email" name="email" value={formData.email} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                    <Field label="Téléphone">
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                  </div>

                  <Field label="Adresse">
                    <textarea name="address" value={formData.address} onChange={handleChange} rows={3} style={TEXTAREA_STYLE} />
                  </Field>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Field label="Ville">
                      <input type="text" name="city" value={formData.city} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                    <Field label="Code postal">
                      <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                    <Field label="Pays (code ISO, ex: FR)">
                      <input
                        type="text"
                        name="country"
                        maxLength={2}
                        value={formData.country}
                        onChange={(e) => handleChange({ target: { name: 'country', value: e.target.value.toUpperCase() } })}
                        style={{ ...INPUT_STYLE, textTransform: 'uppercase' }}
                        placeholder="FR"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Latitude">
                      <input type="number" step="0.000001" name="latitude" value={formData.latitude} onChange={handleChange} style={INPUT_STYLE} placeholder="Ex: 48.8566" />
                    </Field>
                    <Field label="Longitude">
                      <input type="number" step="0.000001" name="longitude" value={formData.longitude} onChange={handleChange} style={INPUT_STYLE} placeholder="Ex: 2.3522" />
                    </Field>
                  </div>
                  <p style={{ ...HINT_STYLE, marginTop: '-8px' }}>
                    Astuce : cherchez votre adresse sur openstreetmap.org, clic droit → "Afficher l'adresse" pour récupérer les coordonnées.
                  </p>

                  <Field label="Description">
                    <textarea name="description" value={formData.description} onChange={handleChange} rows={4} style={TEXTAREA_STYLE} placeholder="Décrivez votre restaurant..." />
                  </Field>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Capacité d'accueil">
                      <input type="number" name="capacity" value={formData.capacity} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                    <Field label="Site web">
                      <input type="url" name="website" value={formData.website} onChange={handleChange} style={INPUT_STYLE} />
                    </Field>
                  </div>

                  {/* Social Media */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <h4 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>Réseaux sociaux</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center gap-3" style={{ background: '#FBF9D9', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 8px 0 16px', minHeight: '48px' }}>
                        <span style={{ flex: 'none', width: '24px', height: '24px', background: '#1877F2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>f</span>
                        </span>
                        <input type="url" name="facebook" value={formData.facebook} onChange={handleChange} placeholder="https://facebook.com/..." style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '13.5px', color: '#051004', outline: 'none' }} />
                      </label>
                      <label className="flex items-center gap-3" style={{ background: '#FBF9D9', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 8px 0 16px', minHeight: '48px' }}>
                        <span style={{ flex: 'none', width: '24px', height: '24px', background: '#E1306C', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>ig</span>
                        </span>
                        <input type="url" name="instagram" value={formData.instagram} onChange={handleChange} placeholder="https://instagram.com/..." style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '13.5px', color: '#051004', outline: 'none' }} />
                      </label>
                      <label className="flex items-center gap-3" style={{ background: '#FBF9D9', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 8px 0 16px', minHeight: '48px' }}>
                        <span style={{ flex: 'none', width: '24px', height: '24px', background: '#1DA1F2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>t</span>
                        </span>
                        <input type="url" name="twitter" value={formData.twitter} onChange={handleChange} placeholder="https://twitter.com/..." style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '13.5px', color: '#051004', outline: 'none' }} />
                      </label>
                      <label className="flex items-center gap-3" style={{ background: '#FBF9D9', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 8px 0 16px', minHeight: '48px' }}>
                        <span style={{ flex: 'none', width: '24px', height: '24px', background: '#0A66C2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>in</span>
                        </span>
                        <input type="url" name="linkedin" value={formData.linkedin} onChange={handleChange} placeholder="https://linkedin.com/..." style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '13.5px', color: '#051004', outline: 'none' }} />
                      </label>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '20px' }}>
                    <div className="flex justify-end gap-3">
                      <button type="button" style={GHOST_BTN}>
                        Annuler
                      </button>
                      <button type="submit" disabled={saving} style={disabledStyle(PRIMARY_BTN, saving)}>
                        {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeSection === 'hours' && (
            <div style={CARD}>
              <div style={CARD_HEADER}>
                <h3 style={CARD_TITLE}>Horaires d'ouverture</h3>
                <p style={CARD_SUBTITLE}>Configurez les horaires de votre restaurant</p>
              </div>
              <div style={CARD_BODY}>
                {Object.entries(hours).map(([day, dayHours]) => (
                  <div key={day} className="flex items-center justify-between gap-3 flex-wrap" style={{ background: '#FBF9D9', borderRadius: '20px', padding: '14px 18px' }}>
                    <div className="flex items-center gap-4">
                      <strong style={{ width: '84px', fontSize: '14px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600 }}>{dayLabels[day]}</strong>
                      <span className="flex items-center gap-2.5">
                        <Toggle
                          checked={!dayHours.closed}
                          onChange={(next) => setHours(prev => ({ ...prev, [day]: { ...prev[day], closed: !next } }))}
                        />
                        <span style={{ fontSize: '12.5px', fontFamily: 'Archivo, sans-serif', fontWeight: 600, color: !dayHours.closed ? PRIMARY : 'rgba(5,16,4,0.5)' }}>
                          {!dayHours.closed ? 'Ouvert' : 'Fermé'}
                        </span>
                      </span>
                    </div>
                    {!dayHours.closed && (
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={dayHours.open}
                          onChange={(e) => setHours(prev => ({ ...prev, [day]: { ...prev[day], open: e.target.value } }))}
                          style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 14px', minHeight: '38px', fontSize: '13px', color: '#051004', outline: 'none' }}
                        />
                        <span style={{ fontSize: '13px', color: 'rgba(5,16,4,0.5)' }}>à</span>
                        <input
                          type="time"
                          value={dayHours.close}
                          onChange={(e) => setHours(prev => ({ ...prev, [day]: { ...prev[day], close: e.target.value } }))}
                          style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '999px', padding: '0 14px', minHeight: '38px', fontSize: '13px', color: '#051004', outline: 'none' }}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
                  <button onClick={handleSubmit} disabled={saving} style={disabledStyle(PRIMARY_BTN, saving)}>
                    {saving ? 'Sauvegarde...' : 'Sauvegarder les horaires'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'delivery' && (
            <div style={CARD}>
              <div style={CARD_HEADER}>
                <h3 style={CARD_TITLE}>Paramètres de livraison</h3>
                <p style={CARD_SUBTITLE}>Configurez les options de livraison</p>
              </div>
              <div style={CARD_BODY}>
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={deliverySettings.enabled}
                    onChange={(next) => setDeliverySettings(prev => ({ ...prev, enabled: next }))}
                  />
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>Activer la livraison</span>
                </div>

                {deliverySettings.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Frais de livraison (€)">
                      <input
                        type="number"
                        step="0.01"
                        value={deliverySettings.fee}
                        onChange={(e) => setDeliverySettings(prev => ({ ...prev, fee: parseFloat(e.target.value) || 0 }))}
                        style={INPUT_STYLE}
                      />
                    </Field>
                    <Field label="Commande minimum (€)">
                      <input
                        type="number"
                        step="0.01"
                        value={deliverySettings.minOrder}
                        onChange={(e) => setDeliverySettings(prev => ({ ...prev, minOrder: parseFloat(e.target.value) || 0 }))}
                        style={INPUT_STYLE}
                      />
                    </Field>
                  </div>
                )}

                <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <h4 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '15px' }}>Modes de paiement acceptés</h4>
                  <div className="flex items-center justify-between gap-3" style={{ background: '#FBF9D9', borderRadius: '20px', padding: '14px 18px' }}>
                    <span style={{ fontSize: '14px' }}>Accepter le paiement à la livraison (espèces)</span>
                    <Toggle
                      checked={deliverySettings.acceptCOD}
                      onChange={(next) => setDeliverySettings(prev => ({ ...prev, acceptCOD: next }))}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3" style={{ background: '#FBF9D9', borderRadius: '20px', padding: '14px 18px' }}>
                    <span style={{ fontSize: '14px' }}>Accepter les commandes à emporter (ramassage)</span>
                    <Toggle
                      checked={deliverySettings.acceptPickup}
                      onChange={(next) => setDeliverySettings(prev => ({ ...prev, acceptPickup: next }))}
                    />
                  </div>
                </div>

                <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
                  <button onClick={handleSubmit} disabled={saving} style={disabledStyle(PRIMARY_BTN, saving)}>
                    {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres de livraison'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'loyalty' && (
            <div style={CARD}>
              <div style={CARD_HEADER}>
                <h3 style={CARD_TITLE}>Programme de fidélité</h3>
                <p style={CARD_SUBTITLE}>
                  Définissez comment les clients gagnent des points namke sur leurs commandes passées chez vous.
                  Les points sont crédités sur leur compte global après paiement.
                </p>
              </div>
              <div style={CARD_BODY}>
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={loyaltySettings.enabled}
                    onChange={(next) => setLoyaltySettings((prev) => ({ ...prev, enabled: next }))}
                  />
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>Activer l'accumulation de points sur les commandes de ce restaurant</span>
                </div>

                {loyaltySettings.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Montant d'une tranche (€)" hint="Ex. : 10 = une tranche tous les 10 € du montant payé.">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={loyaltySettings.trancheEuros}
                        onChange={(e) => setLoyaltySettings((prev) => ({ ...prev, trancheEuros: parseFloat(e.target.value) || 0 }))}
                        style={INPUT_STYLE}
                      />
                    </Field>
                    <Field label="Points par tranche" hint="Ex. : 1 point tous les 10 € → tranche 10 et points 1.">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={loyaltySettings.pointsPerTranche}
                        onChange={(e) => setLoyaltySettings((prev) => ({ ...prev, pointsPerTranche: parseInt(e.target.value, 10) || 0 }))}
                        style={INPUT_STYLE}
                      />
                    </Field>
                  </div>
                )}

                <div style={{ background: '#FBF9D9', borderRadius: '20px', padding: '16px 18px', fontSize: '13.5px', color: 'rgba(5,16,4,0.78)' }}>
                  <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#051004' }}>Aperçu</p>
                  {loyaltySettings.enabled ? (
                    <p style={{ margin: 0 }}>
                      Un client qui paie 35 € gagnerait{' '}
                      <strong>
                        {Math.floor(35 / Math.max(0.01, Number(loyaltySettings.trancheEuros) || 10)) *
                          Math.max(0, parseInt(String(loyaltySettings.pointsPerTranche), 10) || 0)}
                      </strong>{' '}
                      point(s) (arrondi par tranches complètes).
                    </p>
                  ) : (
                    <p style={{ margin: 0 }}>Aucun point ne sera attribué pour les commandes de ce restaurant.</p>
                  )}
                </div>

                <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
                  <button type="button" onClick={saveLoyaltyOnly} disabled={saving} style={disabledStyle(PRIMARY_BTN, saving)}>
                    {saving ? 'Enregistrement...' : 'Enregistrer la fidélité'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'payment' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Wallet Overview */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <h3 style={CARD_TITLE}>Portefeuille</h3>
                  <p style={CARD_SUBTITLE}>Solde disponible et transactions récentes</p>
                </div>
                <div style={CARD_BODY}>
                  {walletInfo ? (
                    <>
                      <div className="flex items-center justify-between" style={{ background: PRIMARY, color: '#FBF9D9', borderRadius: '24px', padding: '22px 24px' }}>
                        <div>
                          <p style={{ margin: '0 0 4px', fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#FFC717' }}>Solde disponible</p>
                          <p style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 2.6vw, 34px)' }}>
                            {walletInfo.balance?.toFixed(2) || '0.00'} €
                          </p>
                        </div>
                        <Wallet size={36} style={{ color: '#FFC717' }} />
                      </div>

                      {recentTransactions.length > 0 && (
                        <div>
                          <span style={{ ...FIELD_LABEL, marginBottom: '12px', display: 'block' }}>Transactions récentes</span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {recentTransactions.slice(0, 5).map((transaction, index) => (
                              <div key={index} className="flex items-center justify-between gap-3" style={{ background: '#FBF9D9', borderRadius: '18px', padding: '12px 16px' }}>
                                <div className="flex items-center gap-3">
                                  {transaction.type === 'credit' ? (
                                    <ArrowDownLeft size={18} style={{ color: PRIMARY }} />
                                  ) : (
                                    <ArrowUpRight size={18} style={{ color: '#A32C0F' }} />
                                  )}
                                  <div>
                                    <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 600 }}>{transaction.description || 'Transaction'}</p>
                                    <p style={{ margin: 0, fontSize: '11.5px', color: 'rgba(5,16,4,0.5)' }}>
                                      {new Date(transaction.created_at).toLocaleDateString('fr-FR')}
                                    </p>
                                  </div>
                                </div>
                                <strong style={{ fontSize: '13.5px', color: transaction.type === 'credit' ? PRIMARY : '#A32C0F' }}>
                                  {transaction.type === 'credit' ? '+' : '-'}{Math.abs(transaction.amount).toFixed(2)} €
                                </strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ margin: 0, textAlign: 'center', padding: '16px 0', fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>Aucune information de portefeuille disponible</p>
                  )}
                </div>
              </div>

              {/* Payment Methods */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <h3 style={CARD_TITLE}>Méthodes de paiement</h3>
                  <p style={CARD_SUBTITLE}>Configurez vos méthodes de paiement acceptées</p>
                </div>
                <div style={CARD_BODY}>
                  <div className="flex items-center justify-between gap-3" style={{ border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', padding: '14px 18px' }}>
                    <div className="flex items-center gap-3">
                      <CreditCard size={22} style={{ color: 'rgba(5,16,4,0.6)' }} />
                      <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Carte bancaire</p>
                        <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Visa, Mastercard, American Express</p>
                      </div>
                    </div>
                    <Toggle checked={true} onChange={() => {}} disabled />
                  </div>

                  <div className="flex items-center justify-between gap-3" style={{ border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', padding: '14px 18px' }}>
                    <div className="flex items-center gap-3">
                      <Wallet size={22} style={{ color: 'rgba(5,16,4,0.6)' }} />
                      <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Paiement en espèces</p>
                        <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Acceptation des paiements en espèces</p>
                      </div>
                    </div>
                    <Toggle checked={true} onChange={() => {}} disabled />
                  </div>
                </div>
              </div>

              {/* Versements : IBAN et identité collectés par Stripe Connect */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <h3 style={CARD_TITLE}>Coordonnées bancaires</h3>
                  <p style={CARD_SUBTITLE}>Votre IBAN est enregistré de façon sécurisée chez notre partenaire de paiement Stripe.</p>
                </div>
                <div style={CARD_BODY}>
                  <a href="/management/wallet" style={{ ...PRIMARY_BTN, display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start', textDecoration: 'none' }}>
                    Gérer mes versements
                  </a>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'certification' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <div className="flex items-center gap-2">
                    <h3 style={CARD_TITLE}>Badge de certification</h3>
                    {certification.isCertified && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#054625', color: '#FBF9D9', fontSize: '11.5px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px' }}>
                        <BadgeCheck size={14} /> Certifié
                      </span>
                    )}
                  </div>
                  <p style={CARD_SUBTITLE}>
                    {certification.isCertified
                      ? "Votre restaurant est certifié et affiche le badge auprès des clients."
                      : "Envoyez vos documents justificatifs pour être vérifié et obtenir le badge de certification, affiché publiquement sur votre fiche restaurant."}
                  </p>
                </div>
                <div style={CARD_BODY}>
                  {[
                    { type: 'kbis', label: 'KBIS / immatriculation' },
                    { type: 'id_card', label: "Pièce d'identité" },
                    { type: 'health_certificate', label: 'Certificat sanitaire' },
                    { type: 'insurance', label: 'Assurance' },
                    { type: 'other', label: 'Autre document' },
                  ].map(({ type, label }) => {
                    const existing = documents.find((d) => d.document_type === type);
                    const isUploading = uploadingDocType === type;
                    return (
                      <div key={type} className="flex items-center justify-between gap-3 flex-wrap" style={{ borderBottom: '1px solid rgba(5,16,4,0.08)', paddingBottom: '16px' }}>
                        <div className="flex items-center gap-3">
                          <span style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#FBF9D9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: PRIMARY }}>
                            <FileText size={18} />
                          </span>
                          <div>
                            <p style={{ margin: 0, fontWeight: 600, fontSize: '14.5px' }}>{label}</p>
                            {existing ? (
                              <div className="flex items-center gap-2">
                                <span style={{
                                  fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '999px',
                                  background: existing.status === 'approved' ? 'rgba(114,183,68,0.18)' : existing.status === 'rejected' ? 'rgba(163,44,15,0.12)' : 'rgba(255,199,23,0.22)',
                                  color: existing.status === 'approved' ? '#054625' : existing.status === 'rejected' ? '#A32C0F' : '#7A5B00',
                                }}>
                                  {existing.status === 'approved' ? 'Approuvé' : existing.status === 'rejected' ? 'Rejeté' : 'En attente de vérification'}
                                </span>
                                <a href={existing.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12.5px', color: PRIMARY }}>
                                  Voir
                                </a>
                              </div>
                            ) : (
                              <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.5)' }}>Aucun document envoyé</p>
                            )}
                            {existing?.status === 'rejected' && existing.rejection_reason && (
                              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#A32C0F' }}>Motif : {existing.rejection_reason}</p>
                            )}
                          </div>
                        </div>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: isUploading ? 'default' : 'pointer', border: '1px solid rgba(5,16,4,0.16)', borderRadius: '999px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: PRIMARY, opacity: isUploading ? 0.6 : 1 }}>
                          <Upload size={14} />
                          {isUploading ? 'Envoi...' : existing ? 'Remplacer' : 'Uploader'}
                          <input
                            type="file"
                            accept="application/pdf,image/jpeg,image/png"
                            className="hidden"
                            disabled={isUploading}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              e.target.value = '';
                              handleDocumentUpload(type, file);
                            }}
                          />
                        </label>
                      </div>
                    );
                  })}
                  {documentsLoading && (
                    <p style={{ margin: 0, fontSize: '13px', color: 'rgba(5,16,4,0.5)' }}>Chargement des documents...</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Change Password */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <h3 style={CARD_TITLE}>Changer le mot de passe</h3>
                  <p style={CARD_SUBTITLE}>Mettez à jour votre mot de passe pour sécuriser votre compte</p>
                </div>
                <div style={CARD_BODY}>
                  <Field label="Mot de passe actuel">
                    <div style={{ position: 'relative' }}>
                      <input
                        type={securitySettings.showPassword ? 'text' : 'password'}
                        value={securitySettings.currentPassword}
                        onChange={(e) => setSecuritySettings(prev => ({ ...prev, currentPassword: e.target.value }))}
                        style={{ ...INPUT_STYLE, paddingRight: '44px' }}
                        placeholder="Entrez votre mot de passe actuel"
                      />
                      <button
                        type="button"
                        onClick={() => setSecuritySettings(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                        style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'rgba(5,16,4,0.5)' }}
                      >
                        {securitySettings.showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </Field>

                  <Field label="Nouveau mot de passe" hint="Minimum 8 caractères avec lettres et chiffres">
                    <input
                      type={securitySettings.showPassword ? 'text' : 'password'}
                      value={securitySettings.newPassword}
                      onChange={(e) => setSecuritySettings(prev => ({ ...prev, newPassword: e.target.value }))}
                      style={INPUT_STYLE}
                      placeholder="Entrez votre nouveau mot de passe"
                    />
                  </Field>

                  <div>
                    <label style={FIELD_LABEL}>Confirmer le nouveau mot de passe</label>
                    <input
                      type={securitySettings.showPassword ? 'text' : 'password'}
                      value={securitySettings.confirmPassword}
                      onChange={(e) => setSecuritySettings(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      style={INPUT_STYLE}
                      placeholder="Confirmez votre nouveau mot de passe"
                    />
                    {securitySettings.newPassword && securitySettings.confirmPassword && (
                      <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: securitySettings.newPassword === securitySettings.confirmPassword ? PRIMARY : '#A32C0F' }}>
                        {securitySettings.newPassword === securitySettings.confirmPassword ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                      </p>
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
                    <button
                      disabled={passwordSaving}
                      onClick={async () => {
                        if (!securitySettings.currentPassword || !securitySettings.newPassword) {
                          toastError('Veuillez remplir tous les champs.');
                          return;
                        }
                        if (securitySettings.newPassword !== securitySettings.confirmPassword) {
                          toastError('Les mots de passe ne correspondent pas.');
                          return;
                        }
                        if (securitySettings.newPassword.length < 8) {
                          toastError('Le mot de passe doit contenir au moins 8 caractères.');
                          return;
                        }
                        setPasswordSaving(true);
                        try {
                          await changePassword(securitySettings.currentPassword, securitySettings.newPassword);
                          toastSuccess('Mot de passe modifié avec succès.');
                          setSecuritySettings(prev => ({
                            ...prev,
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          }));
                        } catch (error) {
                          toastError(error?.message || 'Erreur lors de la modification du mot de passe.');
                        } finally {
                          setPasswordSaving(false);
                        }
                      }}
                      style={disabledStyle(PRIMARY_BTN, passwordSaving)}
                    >
                      {passwordSaving ? 'Modification...' : 'Changer le mot de passe'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Two-Factor Authentication */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <h3 style={CARD_TITLE}>Authentification à deux facteurs</h3>
                  <p style={CARD_SUBTITLE}>Un code à usage unique est envoyé par email à chaque connexion</p>
                </div>
                <div style={CARD_BODY}>
                  <div className="flex items-center justify-between gap-3 flex-wrap" style={{ background: '#FBF9D9', borderRadius: '20px', padding: '14px 18px' }}>
                    <div className="flex items-center gap-3">
                      <Shield size={22} style={{ color: securitySettings.twoFactorEnabled ? PRIMARY : 'rgba(5,16,4,0.35)' }} />
                      <div>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Authentification à deux facteurs (2FA)</p>
                        <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>
                          {securitySettings.twoFactorEnabled ? 'Activée — votre compte est protégé' : 'Désactivée — connexion sans code supplémentaire'}
                        </p>
                      </div>
                    </div>
                    <button
                      disabled={twoFactorLoading}
                      onClick={() => {
                        setTwoFactorModalPassword('');
                        setTwoFactorBackupCodes([]);
                        setShow2FAModal(securitySettings.twoFactorEnabled ? 'disable' : 'enable');
                      }}
                      style={disabledStyle(securitySettings.twoFactorEnabled ? DANGER_GHOST_BTN : { ...PRIMARY_BTN, minHeight: '38px', fontSize: '13px', padding: '0 16px' }, twoFactorLoading)}
                    >
                      {twoFactorLoading ? 'Chargement...' : securitySettings.twoFactorEnabled ? 'Désactiver' : 'Activer'}
                    </button>
                  </div>

                  {/* Backup codes display after enabling */}
                  {twoFactorBackupCodes.length > 0 && (
                    <div className="flex items-start gap-3" style={{ background: '#FEF0EA', border: '1px solid #F9BAA4', borderRadius: '20px', padding: '16px 18px' }}>
                      <AlertCircle size={20} style={{ color: '#A32C0F', flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ fontSize: '13.5px', color: '#7A200B' }}>
                        <p style={{ margin: '0 0 8px', fontWeight: 700 }}>Codes de secours — sauvegardez-les maintenant !</p>
                        <p style={{ margin: '0 0 12px' }}>Ces codes vous permettent de vous connecter si vous ne recevez pas l'email. Ils ne seront plus affichés.</p>
                        <div className="grid grid-cols-2 gap-1" style={{ fontFamily: 'monospace', background: '#FCFBF5', border: '1px solid #F9BAA4', borderRadius: '12px', padding: '12px' }}>
                          {twoFactorBackupCodes.map((code, i) => (
                            <span key={i} style={{ fontSize: '12px', color: '#051004' }}>{code}</span>
                          ))}
                        </div>
                        <button
                          style={{ marginTop: '12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', color: '#7A200B' }}
                          onClick={() => setTwoFactorBackupCodes([])}
                        >
                          J'ai sauvegardé mes codes
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal confirmation 2FA */}
              {show2FAModal && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(5,16,4,0.5)' }}>
                  <div style={{ background: '#FCFBF5', borderRadius: '28px', boxShadow: '0 24px 60px rgba(5,16,4,0.28)', padding: '28px', width: '100%', maxWidth: '420px', margin: '0 16px' }}>
                    <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700, fontSize: '19px' }}>
                      {show2FAModal === 'enable' ? 'Activer la 2FA' : 'Désactiver la 2FA'}
                    </h3>
                    <p style={{ margin: '0 0 18px', fontSize: '13.5px', color: 'rgba(5,16,4,0.62)' }}>
                      {show2FAModal === 'enable'
                        ? 'Confirmez votre mot de passe pour activer la double authentification par email.'
                        : 'Confirmez votre mot de passe pour désactiver la double authentification.'}
                    </p>
                    <input
                      type="password"
                      value={twoFactorModalPassword}
                      onChange={(e) => setTwoFactorModalPassword(e.target.value)}
                      placeholder="Votre mot de passe actuel"
                      style={{ ...INPUT_STYLE, marginBottom: '18px' }}
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('btn-2fa-confirm').click(); }}
                    />
                    <div className="flex gap-3 justify-end">
                      <button onClick={() => setShow2FAModal(null)} style={GHOST_BTN}>
                        Annuler
                      </button>
                      <button
                        id="btn-2fa-confirm"
                        disabled={twoFactorLoading || !twoFactorModalPassword}
                        onClick={async () => {
                          if (!twoFactorModalPassword) return;
                          setTwoFactorLoading(true);
                          try {
                            if (show2FAModal === 'enable') {
                              const result = await enable2FA(twoFactorModalPassword);
                              setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: true }));
                              if (result?.backup_codes) setTwoFactorBackupCodes(result.backup_codes);
                              toastSuccess('Double authentification activée.');
                            } else {
                              await disable2FA(twoFactorModalPassword);
                              setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: false }));
                              toastSuccess('Double authentification désactivée.');
                            }
                            setShow2FAModal(null);
                          } catch (err) {
                            toastError(err?.message || 'Mot de passe incorrect.');
                          } finally {
                            setTwoFactorLoading(false);
                          }
                        }}
                        style={disabledStyle({ ...PRIMARY_BTN, background: show2FAModal === 'disable' ? '#A32C0F' : TERRA, minHeight: '44px' }, twoFactorLoading || !twoFactorModalPassword)}
                      >
                        {twoFactorLoading ? 'En cours...' : 'Confirmer'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <h3 style={CARD_TITLE}>Paramètres de sécurité</h3>
                  <p style={CARD_SUBTITLE}>Gérez les paramètres de sécurité de votre compte</p>
                </div>
                <div style={CARD_BODY}>
                  <div className="flex items-center justify-between gap-3 flex-wrap" style={{ border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', padding: '14px 18px' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Sessions actives</p>
                      <p style={{ margin: 0, fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>Appareils actuellement connectés à votre compte</p>
                    </div>
                    <button
                      disabled={sessionsLoading}
                      onClick={async () => {
                        if (showSessions) { setShowSessions(false); return; }
                        setSessionsLoading(true);
                        try {
                          const data = await getActiveSessions();
                          setSessions(Array.isArray(data) ? data : []);
                          setShowSessions(true);
                        } catch {
                          toastError('Impossible de charger les sessions.');
                        } finally {
                          setSessionsLoading(false);
                        }
                      }}
                      style={disabledStyle({ ...GHOST_BTN, minHeight: '38px', fontSize: '13px', padding: '0 16px' }, sessionsLoading)}
                    >
                      {sessionsLoading ? 'Chargement...' : showSessions ? 'Masquer' : 'Voir les sessions'}
                    </button>
                  </div>

                  {showSessions && (
                    <div style={{ border: '1px solid rgba(5,16,4,0.1)', borderRadius: '20px', overflow: 'hidden' }}>
                      {sessions.length === 0 ? (
                        <p style={{ margin: 0, padding: '16px', fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>Aucune session active trouvée.</p>
                      ) : (
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          {sessions.map((s) => (
                            <li key={s.id} className="flex items-center justify-between gap-3" style={{ padding: '12px 16px', borderBottom: '1px solid rgba(5,16,4,0.06)' }}>
                              <div>
                                <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 600 }}>{s.user_agent || 'Appareil inconnu'}</p>
                                <p style={{ margin: 0, fontSize: '11.5px', color: 'rgba(5,16,4,0.45)' }}>
                                  {s.ip_address || ''}{s.created_at ? ` · Connecté le ${new Date(s.created_at).toLocaleString('fr-FR')}` : ''}
                                </p>
                              </div>
                              <button
                                onClick={async () => {
                                  try {
                                    await terminateSession(s.id);
                                    setSessions(prev => prev.filter(x => x.id !== s.id));
                                    toastSuccess('Session terminée.');
                                  } catch {
                                    toastError('Erreur lors de la déconnexion.');
                                  }
                                }}
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#A32C0F' }}
                              >
                                Déconnecter
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div style={{ padding: '12px 16px', background: '#FBF9D9' }}>
                        <button
                          onClick={async () => {
                            const pwd = prompt('Confirmez votre mot de passe pour déconnecter tous les appareils :');
                            if (!pwd) return;
                            try {
                              await terminateAllSessions(pwd);
                              setSessions([]);
                              setShowSessions(false);
                              toastSuccess('Toutes les sessions ont été fermées.');
                            } catch (e) {
                              toastError(e?.message || 'Erreur.');
                            }
                          }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#A32C0F' }}
                        >
                          Déconnecter tous les appareils
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Email Notifications */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <div className="flex items-center gap-3">
                    <Mail size={20} style={{ color: 'rgba(5,16,4,0.6)' }} />
                    <div>
                      <h3 style={CARD_TITLE}>Notifications par email</h3>
                      <p style={CARD_SUBTITLE}>Recevez des notifications par email</p>
                    </div>
                  </div>
                </div>
                <div style={CARD_BODY}>
                  {Object.entries(notificationSettings.email).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between gap-3" style={{ background: '#FBF9D9', borderRadius: '18px', padding: '12px 16px' }}>
                      <span style={{ fontSize: '13.5px', fontWeight: 600 }}>{notificationLabels[key]}</span>
                      <Toggle
                        checked={value}
                        onChange={(next) => setNotificationSettings(prev => ({ ...prev, email: { ...prev.email, [key]: next } }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Push Notifications */}
              <div style={CARD}>
                <div style={CARD_HEADER}>
                  <div className="flex items-center gap-3">
                    <Bell size={20} style={{ color: 'rgba(5,16,4,0.6)' }} />
                    <div>
                      <h3 style={CARD_TITLE}>Notifications push</h3>
                      <p style={CARD_SUBTITLE}>Recevez des notifications dans votre navigateur</p>
                    </div>
                  </div>
                </div>
                <div style={CARD_BODY}>
                  {Object.entries(notificationSettings.push).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between gap-3" style={{ background: '#FBF9D9', borderRadius: '18px', padding: '12px 16px' }}>
                      <span style={{ fontSize: '13.5px', fontWeight: 600 }}>{notificationLabels[key]}</span>
                      <Toggle
                        checked={value}
                        onChange={(next) => setNotificationSettings(prev => ({ ...prev, push: { ...prev.push, [key]: next } }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div style={{ ...CARD, padding: 'clamp(20px, 2.2vw, 28px)' }}>
                <button
                  onClick={async () => {
                    try {
                      await updateRestaurantProfile({ notification_settings: notificationSettings });
                      toastSuccess('Préférences de notification sauvegardées avec succès.');
                    } catch (error) {
                      toastError('Erreur lors de la sauvegarde des préférences.');
                    }
                  }}
                  style={PRIMARY_BTN}
                >
                  Sauvegarder les préférences
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewRestaurantSettings;
